import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Rocket, Star, FileSignature, Check, ArrowRight, ArrowLeft, 
  Upload, DollarSign, Users, Sparkles, Crown, Shield
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreatorOnboarding = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const plans = [
    {
      id: 'on_ya_royalties',
      name: 'Own Ya Royalties',
      price: 30,
      period: 'year',
      monthlyEquivalent: '2.50',
      icon: <Star className="w-8 h-8" />,
      color: 'from-yellow-400 to-orange-500',
      bgColor: 'from-yellow-500/10 to-orange-500/10',
      borderColor: 'border-yellow-500',
      commission: 0,
      features: [
        'Upload unlimited content',
        'Keep 100% of your earnings',
        'Advanced creator analytics',
        'Priority support',
        'Creator verification badge'
      ],
      bestFor: 'Established creators who want full control',
      highlight: 'MOST POPULAR'
    },
    {
      id: 'contract',
      name: 'The Contract',
      price: 0,
      icon: <FileSignature className="w-8 h-8" />,
      color: 'from-emerald-400 to-cyan-500',
      bgColor: 'from-emerald-500/10 to-cyan-500/10',
      borderColor: 'border-emerald-500',
      commission: 20,
      features: [
        'Upload unlimited content',
        'All premium features included',
        'Creator dashboard & analytics',
        '24/7 priority support',
        'No upfront cost'
      ],
      bestFor: 'New creators starting their journey',
      highlight: 'FREE TO JOIN'
    }
  ];

  const handleSelectPlan = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      if (selectedPlan === 'contract') {
        // Subscribe to The Contract (free)
        await axios.post(
          `${API_URL}/api/subscriptions/subscribe/contract`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast({
          title: 'Welcome to The Contract!',
          description: 'You can now start uploading content. 20% of your sales will go to the platform.',
        });
        
        setStep(3);
      } else if (selectedPlan === 'on_ya_royalties') {
        // Redirect to Stripe checkout
        const response = await axios.post(
          `${API_URL}/api/subscriptions/checkout/on_ya_royalties`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not create checkout session'
          });
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Something went wrong'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete?.();
    onClose();
    // Trigger upload modal
    window.dispatchEvent(new CustomEvent('openUploadModal'));
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center mb-4">
                <Rocket className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Start Your Creator Journey</h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Join thousands of Black creators sharing their talent with the world. 
                Choose a plan that works for you.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <Upload className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white">Upload Content</h4>
                <p className="text-sm text-gray-400">Videos, photos, audio</p>
              </div>
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white">Earn Money</h4>
                <p className="text-sm text-gray-400">Sell your content</p>
              </div>
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <h4 className="font-semibold text-white">Build Community</h4>
                <p className="text-sm text-gray-400">Connect with fans</p>
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              className="w-full bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold py-3"
              data-testid="onboarding-continue-btn"
            >
              Choose Your Plan
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Choose Your Creator Plan</h2>
              <p className="text-gray-400">Select the plan that best fits your needs</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plans.map((plan) => (
                <Card
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`relative cursor-pointer transition-all hover:scale-[1.02] ${
                    selectedPlan === plan.id 
                      ? `bg-gradient-to-br ${plan.bgColor} border-2 ${plan.borderColor} ring-2 ring-offset-2 ring-offset-gray-900 ${plan.borderColor.replace('border-', 'ring-')}` 
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.highlight && (
                    <div className={`absolute top-0 right-0 bg-gradient-to-r ${plan.color} text-black text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg`}>
                      {plan.highlight}
                    </div>
                  )}
                  
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center text-black`}>
                        {plan.icon}
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-lg">{plan.name}</h3>
                        {plan.price === 0 ? (
                          <p className={`text-xl font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>FREE</p>
                        ) : (
                          <div>
                            <p className="text-white">
                              <span className={`text-xl font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent`}>${plan.price}</span>
                              /{plan.period || 'month'}
                            </p>
                            {plan.monthlyEquivalent && (
                              <p className="text-yellow-400 text-sm">that's ${plan.monthlyEquivalent} a month</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {plan.commission > 0 && (
                      <div className="mb-4 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                        <p className="text-xs text-amber-400 font-medium text-center">
                          {plan.commission}% commission on sales
                        </p>
                      </div>
                    )}

                    <div className="space-y-2 mb-4">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="p-2 bg-gray-900/50 rounded-lg">
                      <p className="text-xs text-gray-400 text-center">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        {plan.bestFor}
                      </p>
                    </div>

                    {selectedPlan === plan.id && (
                      <div className="absolute top-3 left-3">
                        <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 border-gray-600 hover:bg-gray-800 text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSelectPlan}
                disabled={!selectedPlan || loading}
                className="flex-1 bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
                data-testid="onboarding-select-plan-btn"
              >
                {loading ? (
                  <span className="flex items-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Processing...
                  </span>
                ) : (
                  <>
                    {selectedPlan === 'contract' ? 'Join The Contract' : 'Subscribe Now'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 text-center">
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center mb-4">
              <Check className="w-12 h-12 text-white" />
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">You&apos;re All Set! 🎉</h2>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                Welcome to the creator community! You can now upload your content and start earning.
              </p>
            </div>

            <div className="p-4 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Crown className="w-6 h-6 text-emerald-400" />
                <span className="text-lg font-semibold text-white">The Contract</span>
              </div>
              <p className="text-sm text-gray-400">
                You have access to all premium features. Start uploading now!
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <Shield className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Secure Platform</p>
              </div>
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Easy Payouts</p>
              </div>
              <div className="p-3 bg-gray-800/50 rounded-lg">
                <Users className="w-6 h-6 text-purple-400 mx-auto mb-1" />
                <p className="text-xs text-gray-400">Growing Community</p>
              </div>
            </div>

            <Button
              onClick={handleComplete}
              className="w-full bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold py-3"
              data-testid="onboarding-complete-btn"
            >
              <Upload className="w-5 h-5 mr-2" />
              Start Uploading Now
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-2 border-yellow-500/30 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-400 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-800" />
        </div>

        {renderStep()}

        {/* Skip Option */}
        {step < 3 && (
          <div className="text-center mt-4">
            <button
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-400 transition-colors"
            >
              Maybe later
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreatorOnboarding;
